import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, XCircle } from 'lucide-react';
import useRefreshLimiter from '../../hooks/useRefreshLimiter';

interface RefreshButtonProps {
    onRefresh: () => void;
    isLoading?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    cooldownTime?: number;
    maxRefreshCount?: number;
    timeWindow?: number;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
    onRefresh,
    isLoading = false,
    size = 'md',
    className = '',
    cooldownTime,
    maxRefreshCount,
    timeWindow
}) => {
    // Usar nuestro hook personalizado
    const {
        isDisabled,
        hasReachedLimit,
        cooldownRemaining,
        executeRefresh
    } = useRefreshLimiter({
        cooldownTime,
        maxRefreshCount,
        timeWindow
    });

    // Determinar el tamaño del icono
    const getIconSize = () => {
        switch (size) {
            case 'sm': return 'w-3 h-3';
            case 'lg': return 'w-5 h-5';
            default: return 'w-4 h-4';
        }
    };

    // Determinar las clases de padding según el tamaño
    const getPaddingClass = () => {
        switch (size) {
            case 'sm': return 'p-1';
            case 'lg': return 'p-2';
            default: return 'p-1.5';
        }
    };

    // Formatear el tiempo de enfriamiento restante
    const formatCooldownTime = () => {
        if (cooldownRemaining <= 0) return '';
        return `${Math.ceil(cooldownRemaining / 1000)}s`;
    };

    // Gestionar el clic en el botón
    const handleClick = () => {
        if (!isLoading && !isDisabled) {
            executeRefresh(onRefresh);
        }
    };

    return (
        <div className="relative inline-block">
            <motion.button
                whileHover={isDisabled || isLoading ? undefined : { scale: 1.1 }}
                whileTap={isDisabled || isLoading ? undefined : { rotate: 180, transition: { duration: 0.3 } }}
                onClick={handleClick}
                disabled={isDisabled || isLoading}
                className={`
          ${getPaddingClass()} 
          rounded-full 
          transition-colors
          ${isDisabled
                        ? hasReachedLimit
                            ? 'text-red-400 cursor-not-allowed'
                            : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : isLoading
                            ? 'text-gray-400 cursor-wait'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
          ${className}
        `}
                title={
                    hasReachedLimit
                        ? 'Demasiados refrescos, espera unos minutos'
                        : isDisabled
                            ? `Espera ${formatCooldownTime()} para refrescar`
                            : isLoading
                                ? 'Cargando...'
                                : 'Refrescar datos'
                }
                aria-label="Refrescar"
            >
                {isLoading ? (
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                        <RefreshCw className={getIconSize()} />
                    </motion.div>
                ) : hasReachedLimit ? (
                    <XCircle className={getIconSize()} />
                ) : (
                    <RefreshCw className={getIconSize()} />
                )}
            </motion.button>

            {/* Indicador de enfriamiento */}
            {isDisabled && !hasReachedLimit && cooldownRemaining > 0 && (
                <span className="absolute -top-1 -right-1 text-[8px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full px-1 min-w-4 text-center">
                    {Math.ceil(cooldownRemaining / 1000)}
                </span>
            )}
        </div>
    );
};

export default RefreshButton;